/**
 * 🔴 W72 STEP 4 — THE PROGRESS TAB, AS DERIVATIONS (Proposal 4 with his chart note). Every figure the tab prints is
 * computed here, pure, and executed by `progressPage.test.ts`; the tab (`routes/ClientProgress.tsx`) only places it.
 * Each derivation reads the ONE reader that already answers its question — the tiles ask `bodyMetrics.reading` and
 * `client.windows`, the lift rows ask the chart's own twin (`progressChart.ts`), the goal rows ask `goalGaps`, the strip
 * asks `coverage.summarise` — so no figure here is a second answer to a question another screen already answers.
 *
 * What the proposal named and what is built, said: "sessions this block" is SESSIONS IN THE LAST 30 DAYS (the web has
 * no block for a coach-assigned calendar; the window is `client.windows`' own), and "sets done as written" is WORKING
 * SETS LOGGED IN THE LAST 30 DAYS (a comparison against what was written needs the assigned copies' sets, a read this
 * tab does not make) — both labelled for what they count.
 */

import { reading, whenSaid, type Measurement } from "./bodyMetrics";
import type { WindowCount } from "./client";
import { EMPTY_MARK, isEmptyWindow } from "./emptyMark";
import {
  allowed, fieldsFor, fieldView, numberText, perUnit, resolvedField, seriesPoints, sessionDay, unitFor, valueText, type Field,
} from "./progressChart";
import { readoutLine, type ExerciseHistory } from "./progressHistory";
import { ENGINE_RULE, ruleBodyIsLoad, type OneRMRule, type WeighIn } from "./oneRepMax";
import type { CellReading } from "./coverage";
import { cellLabel, gapStateLabel, type RequirementReading } from "./goalGaps";

const DAY = 86_400_000;

export type Tile = { readonly key: string; readonly label: string; readonly value: string; readonly unit: string; readonly sub: string };

export function bodyweightTile(rows: Measurement[], today: Date): Tile {
  const r = reading(rows, (m) => m.weightKg, "kg", today);
  if (r.kind === "none") return { key: "bodyweight", label: "Bodyweight", value: EMPTY_MARK, unit: "", sub: "Nothing in the last four weeks" };
  if (r.kind === "one") return { key: "bodyweight", label: "Bodyweight", value: numberText(r.value), unit: "kg", sub: `One reading, ${whenSaid(r.ageDays)}` };
  const sign = r.change > 0 ? "+" : r.change < 0 ? "−" : "±";
  return { key: "bodyweight", label: "Bodyweight", value: numberText(r.value), unit: "kg",
    sub: `${sign}${numberText(Math.abs(r.change))} kg in four weeks` };
}

export function sessionsTile(last30: WindowCount): Tile {
  // THE ONE PREDICATE for "nothing in this window" (`emptyMark.isEmptyWindow`), the roster's and the old counters'.
  if (isEmptyWindow({ completed: last30.trained, assigned: last30.assigned })) {
    return { key: "sessions", label: "Sessions · 30 days", value: EMPTY_MARK, unit: "", sub: "Nothing assigned in this window" };
  }
  return { key: "sessions", label: "Sessions · 30 days", value: `${last30.trained}`, unit: `/ ${last30.assigned}`,
    sub: "trained of assigned" };
}

export function setsTile(histories: readonly ExerciseHistory[], now: number): Tile {
  const from = now - 30 * DAY;
  let sets = 0, days = new Set<number>();
  for (const h of histories) for (const e of h.entries) {
    if (e.date < from || e.date > now) continue;
    sets += e.sets;
    days = days.add(new Date(e.date).setHours(0, 0, 0, 0));
  }
  if (sets === 0) return { key: "sets", label: "Working sets · 30 days", value: EMPTY_MARK, unit: "", sub: "No working set logged" };
  return { key: "sets", label: "Working sets · 30 days", value: String(sets), unit: "", sub: `over ${days.size} day${days.size === 1 ? "" : "s"}` };
}

/** One lift's move over its whole history, in its default field (the chart's first offered field). */
export type Move = { readonly field: Field; readonly first: number; readonly last: number; readonly delta: number; readonly days: number };

export function liftMove(history: ExerciseHistory, progressViews: readonly string[], rule: OneRMRule = ENGINE_RULE,
                         weighIns: readonly WeighIn[] = []): Move | null {
  // W125: a coach's switch OFF means the row never states the 1RM; ON reads the exact estimate (`oneRepMax.ts`).
  const field = resolvedField(null, allowed(fieldsFor(history.entries, rule), progressViews, rule));
  if (field === null) return null;
  const pts = seriesPoints(field, history.entries, rule, weighIns);
  const first = pts[0], last = pts[pts.length - 1];
  if (first === undefined || last === undefined) return null;
  return { field, first: first.value, last: last.value, delta: last.value - first.value, days: pts.length };
}

/** The largest RELATIVE rise of any lift with at least two days — "the block's headline lift move". */
export function biggestMoveTile(histories: readonly ExerciseHistory[], views: (id: string) => readonly string[],
                                rules: (id: string) => OneRMRule = () => ENGINE_RULE, weighIns: readonly WeighIn[] = [],
                                km: (id: string) => boolean = () => false): Tile {
  let best: { h: ExerciseHistory; m: Move; rel: number } | null = null;
  for (const h of histories) {
    const m = liftMove(h, views(h.id), rules(h.id), weighIns);
    if (m === null || m.days < 2 || m.first <= 0 || m.delta <= 0) continue;
    const rel = m.delta / m.first;
    if (best === null || rel > best.rel || (rel === best.rel && h.name < best.h.name)) best = { h, m, rel };
  }
  if (best === null) return { key: "move", label: "Biggest move", value: EMPTY_MARK, unit: "", sub: "No lift has moved up yet" };
  // W136 B2 S3r5 P2 — a km run's move says km (`unitFor` / `perUnit`); the RELATIVE rise above is unit-free.
  const kmBest = km(best.h.id);
  const u = unitFor(best.m.field, kmBest);
  return { key: "move", label: "Biggest move", value: `+${numberText(best.m.delta / perUnit(best.m.field, kmBest))}`, unit: u,
    sub: `${best.h.name} · ${fieldView(best.m.field).label}` };
}

/* ── LIFTS AND HOLDS ─────────────────────────────────────────────────────────────────────────── */

export type LiftRow = {
  readonly id: string;
  readonly name: string;
  /** The latest day in the prescription's words (`SessionReadout.line`). */
  readonly dose: string;
  readonly lastDone: string;
  /** The default field's latest value, in its unit, or the empty mark. */
  readonly last: string;
  /** Since the first day: `+2.8 kg`, `−1 reps`, or null for "—" (one day, or no change). */
  readonly change: string | null;
  readonly rising: boolean;
};

/** The lift row's "last done" — the chart's own `sessionDay`, so the row and the readout say one date one way. */
export const shortDay = sessionDay;

export function liftRows(histories: readonly ExerciseHistory[], views: (id: string) => readonly string[],
                         rules: (id: string) => OneRMRule = () => ENGINE_RULE, weighIns: readonly WeighIn[] = [],
                         km: (id: string) => boolean = () => false): LiftRow[] {
  return histories.map((h) => {
    const latest = h.entries[h.entries.length - 1];
    const rule = rules(h.id);
    const isKm = km(h.id);
    const raw = liftMove(h, views(h.id), rule, weighIns);
    // W136 B2 S3r5 P2 — a km run's row says km: the move's numbers in the FIELD's unit (`perUnit`), never by magnitude.
    const per = raw === null ? 1 : perUnit(raw.field, isKm);
    const m = raw === null || per === 1 ? raw : { ...raw, first: raw.first / per, last: raw.last / per, delta: raw.delta / per };
    // W125: an added load (the body is part of it) is said with its `+`.
    const plus = m !== null && m.field === "oneRM" && ruleBodyIsLoad(rule) ? "+" : "";
    const unit = m === null ? "" : unitFor(m.field, isKm);
    const change = m === null || m.days < 2 || Math.abs(m.delta) < 1e-9 ? null
      : `${m.delta > 0 ? "+" : "−"}${numberText(Math.abs(m.delta))} ${unit}`;
    return {
      id: h.id, name: h.name,
      dose: latest === undefined ? "" : readoutLine(latest.performed, isKm),
      lastDone: latest === undefined ? "" : shortDay(latest.date),
      last: m === null ? EMPTY_MARK : plus + valueText(m.last, unit),
      change, rising: m !== null && m.delta > 0,
    };
  });
}

/** The rows the list shows before "All N ›": the first six. */
export const LIFTS_SHOWN = 6;

/** The rows on screen: the first six, or all of them when asked — AND all of them when the address opens a lift past
 *  the sixth, because a link to a lift that draws nothing is a dead link (found rendering his own Weighted Pull-Up). */
export function visibleLifts<T extends { readonly id: string }>(rows: readonly T[], allLifts: boolean, lift: string | null): T[] {
  const at = lift === null ? -1 : rows.findIndex((r) => r.id === lift);
  return allLifts || at >= LIFTS_SHOWN ? [...rows] : rows.slice(0, LIFTS_SHOWN);
}

/* ── GOALS ───────────────────────────────────────────────────────────────────────────────────── */

export type GoalRow = {
  readonly goal: string;
  readonly met: number;
  readonly required: number;
  /** "4 of 6 ranges covered this week". */
  readonly standing: string;
  /** The most pressing uncovered range for this goal, said — or null when every range is covered. */
  readonly truth: string | null;
};

const STATE_ORDER = ["blockedByDeferral", "zero", "thin", "unknown", "metAmbiguous", "met"] as const;

/** One row per recognised goal, in the athlete's own ranked order, from the goal-gap report's own readings. */
export function goalRows(goals: readonly string[], readings: readonly RequirementReading[]): GoalRow[] {
  return goals.map((goal) => {
    const mine = readings.filter((r) => r.askedBy.includes(goal));
    const met = mine.filter((r) => r.state === "met" || r.state === "metAmbiguous").length;
    const worst = [...mine].sort((a, b) => STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state))[0];
    const truth = worst === undefined || worst.state === "met" || worst.state === "metAmbiguous"
      ? null : `${gapStateLabel(worst.state)}: ${cellLabel(worst.action, worst.reading.zone).toLowerCase()}`;
    return {
      goal, met, required: mine.length,
      standing: mine.length === 0 ? "No range this app measures" : `${met} of ${mine.length} ranges covered this week`,
      truth,
    };
  }).filter((r) => r.required > 0);
}

/* ── THE COVERAGE STRIP ──────────────────────────────────────────────────────────────────────── */

export type StripCell = { readonly key: string; readonly tone: "covered" | "thin" | "gap" | "unknown" | "declared" };

export type Strip = {
  readonly covered: number; readonly thin: number; readonly gaps: number; readonly notYetKnown: number;
  readonly cells: readonly StripCell[];
};

export function toneOf(r: CellReading): StripCell["tone"] {
  switch (r.state) {
    case "covered": return "covered";
    case "thin": return "thin";
    case "zeroAccidental": return "gap";
    case "provisional": return "unknown";
    case "zeroDeclared": case "declaredButTrained": return "declared";
  }
}

/** The four counts — over EVERY reading, so they equal the map's own `summarise` line — and one cell per reading, in taxonomy order. */
export function coverageStrip(shown: readonly CellReading[]): Strip {
  const count = (t: StripCell["tone"]) => shown.filter((r) => toneOf(r) === t).length;
  return {
    covered: count("covered"), thin: count("thin"), gaps: count("gap"), notYetKnown: count("unknown"),
    cells: shown.map((r) => ({ key: r.key, tone: toneOf(r) })),
  };
}

/* ── THE TAB'S ADDRESS — every view of it is one URL, so a still (and a pasted link) can pin it ─────── */

export type ProgressAddress = {
  /** The lift whose full chart is open on its row. */
  readonly lift: string | null;
  /** Every lift, not the first six. */
  readonly allLifts: boolean;
  /** The one disclosure open under the tiles, goals or strip. */
  readonly open: "body" | "coverage" | "goals" | null;
  /** The chart's field menu open at first paint (a still's pin; never written). */
  readonly fieldMenu: boolean;
};

const OPEN = ["body", "coverage", "goals"] as const;

export function progressAddress(q: Record<string, string>): ProgressAddress {
  const lift = q["lift"] ?? "";
  const open = q["open"] ?? "";
  return {
    lift: lift === "" ? null : lift,
    allLifts: q["lifts"] === "all",
    open: (OPEN as readonly string[]).includes(open) ? (open as ProgressAddress["open"]) : null,
    fieldMenu: q["menu"] === "field",
  };
}

/** The panel's own keys, in one order; the default view writes nothing. */
export function progressQuery(a: Omit<ProgressAddress, "fieldMenu">): string {
  return [
    a.lift === null ? "" : `lift=${encodeURIComponent(a.lift)}`,
    a.allLifts ? "lifts=all" : "",
    a.open === null ? "" : `open=${a.open}`,
  ].filter((k) => k !== "").join("&");
}

/* FIX 3 (Fable, step 4): ONE proportional bar, four segments (covered ink · thin grey · gaps accent · not yet known
   dashed; a declared zero is a fifth, quiet segment), the counts under it — a grid of 118 tiny cells was noise. */
export type Segment = { readonly tone: StripCell["tone"]; readonly count: number; readonly share: number };

export function stripSegments(strip: Strip): Segment[] {
  const total = strip.cells.length;
  if (total === 0) return [];
  const order: StripCell["tone"][] = ["covered", "thin", "gap", "unknown", "declared"];
  return order
    .map((tone) => ({ tone, count: strip.cells.filter((c) => c.tone === tone).length }))
    .filter((s) => s.count > 0)
    .map((s) => ({ ...s, share: s.count / total }));
}
